import { ActionIcon, Anchor, AppShell, Box, Burger, Button, Group, Modal, NavLink, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageWidthContext } from "./common/page-width";
import { ShareStateProvider, useShareStateContext } from "./common/share-state";
import { useTabFlag } from "./common/tab-state";
import { IconBrandGithub, IconCheck, IconChevronLeft, IconChevronRight, IconLink, IconRestore, IconServerCog } from "./icons";
import { PageArticle } from "./page-article";
import { crumbs } from "./page-document";
import { CATEGORIES, HOME_PATH, isPagePath, isUtilityPath, PAGE_META } from "./page-meta";
import { UtilitySpotlight } from "./spotlight";
import { ATTRIBUTIONS_PATH, utilities } from "./utility-registry";

interface LayoutProps {
  children?: ReactNode;
}

const MAIN_CONTENT_ID = "main-content";

// The address this document was served from, which is the whole of what the shell has to know about where it is:
// every move between pages is a load of its own, so nothing here can change under it.
const PATH = window.location.pathname;

function ResetStateButton({ onReset }: { onReset: () => void }) {
  const ctx = useShareStateContext();
  const [opened, { open, close }] = useDisclosure(false);

  const handleReset = useCallback(() => {
    ctx?.clearHash();
    onReset();
    close();
  }, [close, ctx, onReset]);

  return (
    <>
      <Tooltip label="Reset state" withArrow>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={open}
          size="lg"
          aria-label="Reset state"
        >
          <IconRestore size="1.2rem" />
        </ActionIcon>
      </Tooltip>
      <Modal opened={opened} onClose={close} title="Reset state?" centered>
        <Stack gap="lg">
          <Text size="sm">
            This puts the page back to how it opens fresh and drops the shared link from the address bar. Anything
            entered here is lost unless the link was copied.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close}>Cancel</Button>
            <Button color="red" onClick={handleReset}>Reset</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function CopyStateButton() {
  const ctx = useShareStateContext();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!ctx) return;
    const url = ctx.getShareUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [ctx]);

  return (
    <Tooltip label={copied ? "Copied!" : "Copy state link"} withArrow>
      <ActionIcon
        variant="subtle"
        color={copied ? "teal" : "gray"}
        onClick={handleCopy}
        size="lg"
      >
        {copied ? <IconCheck size="1.2rem" /> : <IconLink size="1.2rem" />}
      </ActionIcon>
    </Tooltip>
  );
}

// Every page but the welcome one, whose body is this list already, ends on a link to every utility under its group:
// the navbar is the same links, but a reader who has hidden it or scrolled to the end of an article is here instead.
function SiteFooter({ directory }: { directory: boolean }) {
  return (
    <Box component="footer" className="site-footer">
      {directory && <SiteDirectory />}
      <Group justify="space-between" py="md" gap="sm">
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            ©{" "}
            <Anchor
              href="https://manualpilot.com"
              target="_blank"
              rel="noopener noreferrer"
              inherit
              c="dimmed"
              underline="hover"
            >
              Manualpilot
            </Anchor>{" "}
            {new Date().getFullYear()}
          </Text>
          <Text size="sm" c="dimmed">·</Text>
          <Anchor href={ATTRIBUTIONS_PATH} size="sm" c="dimmed" underline="hover">Attributions</Anchor>
        </Group>
        <Anchor
          href="https://github.com/manualpilot/utils.plus"
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          c="dimmed"
          underline="hover"
        >
          <Group gap={6} wrap="nowrap">
            <IconBrandGithub size="1rem" stroke={1.5} />
            <Text size="sm" ff="monospace">manualpilot/utils.plus</Text>
          </Group>
        </Anchor>
      </Group>
    </Box>
  );
}

// Not a <nav>: the navbar is already the landmark holding these links, and a second one of the same is noise to
// anybody moving between landmarks.
function SiteDirectory() {
  return (
    <Box className="site-directory">
      {CATEGORIES.map(({ name, paths }) => (
        <Box key={name}>
          <Text component="h2" size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">{name}</Text>
          <Stack component="ul" gap={4} className="site-directory-list">
            {paths.map((path) => (
              <li key={path}>
                <Anchor href={path} size="sm" c="dimmed" underline="hover">{PAGE_META[path].label}</Anchor>
              </li>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

// The site's name and, on any page but the welcome one, the page's own: the trail the document's graph describes,
// drawn where the name already was rather than as a row of its own above every page.
function SiteCrumbs() {
  const page = isPagePath(PATH) ? crumbs(PATH).at(-1) : undefined;

  return (
    <Box component="nav" aria-label="Breadcrumb" className="site-crumbs">
      <ol>
        <li>
          {/* A link rather than a clickable group, so the way home can be opened in a tab like any other. */}
          <UnstyledButton component="a" href={HOME_PATH} className="site-home">
            <IconServerCog size={28} />
            <span className="site-name">utils+</span>
          </UnstyledButton>
        </li>
        {page && (
          <li className="site-crumb">
            <Text component="span" size="lg" c="dimmed" truncate aria-current="page">{page.label}</Text>
          </li>
        )}
      </ol>
    </Box>
  );
}

// A Tab pressed before the first commit lands on a link in the document's own body, which React then clears, and the
// focus falls to nothing. The skip link is where that Tab would have gone, so the first commit puts it there instead.
let tabbedEarly = !!document.activeElement?.closest(".page-fallback");
document.getElementById("root")?.addEventListener("focusin", (event) => {
  if ((event.target as Element).closest(".page-fallback")) tabbedEarly = true;
});

function useEarlyFocus(skip: RefObject<HTMLButtonElement | null>) {
  useEffect(() => {
    if (tabbedEarly && document.activeElement === document.body) skip.current?.focus();
    tabbedEarly = false;
  }, [skip]);
}

function NavbarNotch({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  const label = shown ? "Hide the navigation" : "Show the navigation";

  return (
    <Tooltip label={label} withArrow position="right">
      <UnstyledButton className="navbar-notch" onClick={onToggle} aria-label={label} aria-expanded={shown}>
        {shown ? <IconChevronLeft size="0.85rem" stroke={2} /> : <IconChevronRight size="0.85rem" stroke={2} />}
      </UnstyledButton>
    </Tooltip>
  );
}

export function Layout({ children }: LayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  // The navbar and the width cap are the shell's rather than any page's, and this tab is where the two choices live:
  // a page's own state goes with its document now, and neither of these is a page's.
  const [navShown, toggleNav] = useTabFlag("navbar-shown", true);
  const [wide, toggleWide] = useTabFlag("page-wide", false);
  const [stateKey, setStateKey] = useState(0);
  const pageWidth = useMemo(() => ({ wide, toggle: toggleWide }), [wide, toggleWide]);

  // Asked of the registry rather than of "not the welcome page": attributions has no state to share or reset either.
  const isUtilityPage = utilities.some((utility) => utility.path === PATH);

  const skip = useRef<HTMLButtonElement>(null);
  useEarlyFocus(skip);

  const handleReset = useCallback(() => setStateKey((key) => key + 1), []);

  return (
    <ShareStateProvider>
      <PageWidthContext.Provider value={pageWidth}>
        <AppShell
          header={{ height: 60 }}
          navbar={{ width: 250, breakpoint: "sm", collapsed: { mobile: !opened, desktop: !navShown } }}
          padding="md"
        >
          {/* Mounted on every page, since the shortcut is the shell's; what it opens is a modal in a portal of its own. */}
          <UtilitySpotlight />

          {/* The first stop of the page, moving focus itself: a fragment link would write over the share link. */}
          <UnstyledButton
            ref={skip}
            className="skip-link"
            onClick={() => document.getElementById(MAIN_CONTENT_ID)?.focus()}
          >
            Skip to the utility
          </UnstyledButton>

          <AppShell.Header>
            <Group h="100%" px="md" justify="space-between" wrap="nowrap">
              <Group gap={0} wrap="nowrap" miw={0} flex={1}>
                <Burger
                  opened={opened}
                  onClick={toggle}
                  hiddenFrom="sm"
                  size="sm"
                  mr="sm"
                  aria-label={opened ? "Close the navigation" : "Open the navigation"}
                />
                <SiteCrumbs />
              </Group>
              {isUtilityPage && (
                <Group gap="xs">
                  <ResetStateButton onReset={handleReset} />
                  <CopyStateButton />
                </Group>
              )}
            </Group>
          </AppShell.Header>

          {/* Rides the navbar's own edge rather than the header, and so is reached before the links it governs. */}
          <NavbarNotch shown={navShown} onToggle={toggleNav} />

          {/* The attribute is what global.css takes the collapsed navbar out of the tab order with. */}
          <AppShell.Navbar
            p="md"
            className="app-navbar"
            data-collapsed-mobile={!opened || undefined}
            data-collapsed-desktop={!navShown || undefined}
          >
            {/* The links scroll within the navbar's height, which is how a short window reaches the last of them. */}
            <Box className="navbar-links">
              {utilities.map(({ path, label, Icon }) => (
                // A plain anchor, the whole of what a link needs to be now that following one is a load of its own.
                <NavLink
                  key={path}
                  component="a"
                  href={path}
                  label={label}
                  leftSection={<Icon size="1rem" stroke={1.5} />}
                  active={PATH === path}
                  style={{ borderRadius: "var(--mantine-radius-md)" }}
                />
              ))}
            </Box>
          </AppShell.Navbar>

          <AppShell.Main className="main-region">
            {/* Focusable only as the skip link's target: a Tab from here carries on into the utility's own controls. */}
            <Box className="main-container" id={MAIN_CONTENT_ID} tabIndex={-1} data-wide={wide || undefined}>
              {/* A utility's state is its own, so a reset is a remount: it reads its defaults back off a cleared hash. */}
              <div className="page-tool" key={stateKey}>{children}</div>
              {isUtilityPath(PATH) && <PageArticle path={PATH} />}
              <SiteFooter directory={PATH !== HOME_PATH} />
            </Box>
          </AppShell.Main>
        </AppShell>
      </PageWidthContext.Provider>
    </ShareStateProvider>
  );
}
